import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, Download, X } from 'lucide-react';
import api from '../../lib/api';

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const statusLabel = (status) => {
  const map = {
    draft: 'Draft',
    sent: 'Sent',
    accepted: 'Accepted',
    rejected: 'Rejected',
    converted: 'Converted',
  };
  return map[status] || status;
};

const statusColor = (status) => {
  const map = {
    draft: 'text-gray-600',
    sent: 'text-blue-600',
    accepted: 'text-blue-600',
    rejected: 'text-red-600',
    converted: 'text-purple-700',
  };
  return map[status] || 'text-gray-600';
};

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-800 font-medium">{value}</span>
  </div>
);

const QuotationPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    Promise.all([api.get(`/quotations/${id}`), api.get('/tenant/profile')])
      .then(([qRes, pRes]) => {
        setQuotation(qRes.data);
        setProfile(pRes.data);
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to load quotation.');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <p className="text-gray-500 text-lg">Loading quotation...</p>
      </div>
    );
  }

  if (error || !quotation || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <p className="text-red-500 text-lg">{error || 'Quotation not found.'}</p>
      </div>
    );
  }

  const subtotal = (quotation.items || []).reduce((s, it) => {
    const q = parseFloat(it.quantity) || 0;
    const p = parseFloat(it.unit_price) || 0;
    const d = parseFloat(it.discount_percent) || 0;
    return s + q * p * (1 - d / 100);
  }, 0);
  const wmText = statusLabel(quotation.status).toUpperCase();
  const showWm = ['accepted', 'rejected', 'converted'].includes(quotation.status);

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; background: white; }
          @page { size: A4; margin: 12mm 14mm; }
          .quotation-wrapper { padding-top: 0 !important; }
          .watermark { position: fixed !important; }
        }
        .quotation-wrapper { font-family: 'Segoe UI', Arial, sans-serif; }
      `}</style>

      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-gray-100 border-b border-gray-300 px-6 py-3 flex items-center gap-3 shadow-sm">
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          data-testid="print-button"
        >
          <Printer className="w-4 h-4" />
          Print Quotation
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-white border border-[#e5e7eb] text-[#1a1a2e] hover:bg-[#f9fafb] px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
        <div className="ml-auto">
          <button
            onClick={() => window.close()}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-800 text-sm"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </div>

      <div
        className="quotation-wrapper bg-white min-h-screen pt-16 pb-12"
        data-testid="quotation-print-wrapper"
      >
        <div className="relative max-w-[820px] mx-auto px-10 py-8">
          {showWm && (
            <div
              className="watermark pointer-events-none fixed inset-0 flex items-center justify-center"
              style={{ zIndex: 0 }}
            >
              <span
                style={{
                  transform: 'rotate(-45deg)',
                  fontSize: '130px',
                  fontWeight: '900',
                  color: 'rgba(107, 33, 168, 0.08)',
                  letterSpacing: '8px',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {wmText}
              </span>
            </div>
          )}

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div className="flex justify-between items-start mb-8">
              <div>
                <h1 className="text-2xl font-bold text-[#1E40AF] leading-tight">
                  {profile.company_name}
                </h1>
                {profile.company_address && (
                  <p className="text-sm text-gray-600 mt-1">{profile.company_address}</p>
                )}
                {profile.company_city && (
                  <p className="text-sm text-gray-600">{profile.company_city}</p>
                )}
                {profile.company_phone && (
                  <p className="text-sm text-gray-600">{profile.company_phone}</p>
                )}
                {profile.company_ntn && (
                  <p className="text-sm text-gray-600">NTN: {profile.company_ntn}</p>
                )}
              </div>

              <div className="text-right">
                <h2
                  className="font-extrabold text-[#1E40AF] tracking-widest"
                  style={{ fontSize: '52px', lineHeight: 1 }}
                >
                  QUOTATION
                </h2>
                <p
                  className="text-base font-semibold text-gray-700 mt-2"
                  data-testid="print-quotation-number"
                >
                  {quotation.quotation_number}
                </p>
              </div>
            </div>

            <hr className="border-[#1E40AF] border-t-2 mb-6" />

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="border border-gray-200 rounded overflow-hidden">
                <div className="bg-[#1E40AF] text-white px-4 py-2 text-sm font-semibold">
                  Quotation Details
                </div>
                <div className="px-4 py-3 text-sm space-y-2">
                  <Row label="Quotation No" value={quotation.quotation_number} />
                  <Row label="Issue Date" value={fmtDate(quotation.issue_date)} />
                  <Row label="Expiry Date" value={fmtDate(quotation.expiry_date)} />
                  <Row
                    label="Status"
                    value={
                      <span className={`font-semibold ${statusColor(quotation.status)}`}>
                        {statusLabel(quotation.status)}
                      </span>
                    }
                  />
                </div>
              </div>

              <div className="border border-gray-200 rounded overflow-hidden">
                <div className="bg-[#1E40AF] text-white px-4 py-2 text-sm font-semibold">
                  Quotation To
                </div>
                <div className="px-4 py-3 text-sm">
                  <p className="font-semibold text-gray-800 text-base">
                    {quotation.customer?.name || '—'}
                  </p>
                  {quotation.customer?.address && (
                    <p className="text-gray-600 mt-1">{quotation.customer.address}</p>
                  )}
                  {quotation.customer?.city && (
                    <p className="text-gray-600">{quotation.customer.city}</p>
                  )}
                  {quotation.customer?.phone && (
                    <p className="text-gray-600 mt-1">{quotation.customer.phone}</p>
                  )}
                  {quotation.customer?.ntn && (
                    <p className="text-gray-600">NTN: {quotation.customer.ntn}</p>
                  )}
                </div>
              </div>
            </div>

            <table className="w-full text-sm mb-8" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#1E40AF', color: 'white' }}>
                  <th className="py-3 px-3 text-left" style={{ width: '42px' }}>
                    Sr No
                  </th>
                  <th className="py-3 px-3 text-left">Product Name</th>
                  <th className="py-3 px-3 text-right" style={{ width: '70px' }}>
                    Qty
                  </th>
                  <th className="py-3 px-3 text-left" style={{ width: '70px' }}>
                    Unit
                  </th>
                  <th className="py-3 px-3 text-right" style={{ width: '90px' }}>
                    Unit Price
                  </th>
                  <th className="py-3 px-3 text-right" style={{ width: '60px' }}>
                    Disc%
                  </th>
                  <th className="py-3 px-3 text-right" style={{ width: '100px' }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {(quotation.items || []).map((item, idx) => {
                  const qty = parseFloat(item.quantity) || 0;
                  const price = parseFloat(item.unit_price) || 0;
                  const disc = parseFloat(item.discount_percent) || 0;
                  const total = qty * price * (1 - disc / 100);
                  return (
                    <tr
                      key={item.id || idx}
                      style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#F9FAFB' }}
                    >
                      <td
                        className="py-2 px-3 text-gray-600"
                        style={{ borderBottom: '1px solid #F0F0F0' }}
                      >
                        {idx + 1}
                      </td>
                      <td
                        className="py-2 px-3 font-medium text-gray-800"
                        style={{ borderBottom: '1px solid #F0F0F0' }}
                      >
                        {item.product?.name || '—'}
                      </td>
                      <td
                        className="py-2 px-3 text-right text-gray-700"
                        style={{ borderBottom: '1px solid #F0F0F0' }}
                      >
                        {qty % 1 === 0 ? qty : qty.toFixed(2)}
                      </td>
                      <td
                        className="py-2 px-3 text-gray-600"
                        style={{ borderBottom: '1px solid #F0F0F0' }}
                      >
                        {item.unit_name || item.product?.unit || '—'}
                      </td>
                      <td
                        className="py-2 px-3 text-right text-gray-700"
                        style={{ borderBottom: '1px solid #F0F0F0' }}
                      >
                        {price.toFixed(2)}
                      </td>
                      <td
                        className="py-2 px-3 text-right text-gray-700"
                        style={{ borderBottom: '1px solid #F0F0F0' }}
                      >
                        {disc.toFixed(2)}
                      </td>
                      <td
                        className="py-2 px-3 text-right font-semibold text-gray-800"
                        style={{ borderBottom: '1px solid #F0F0F0' }}
                      >
                        {total.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex justify-end mb-8">
              <div className="w-full max-w-xs border border-gray-200 rounded overflow-hidden">
                <div className="bg-[#1E40AF] text-white px-4 py-2 text-sm font-semibold">
                  Summary
                </div>
                <div className="px-4 py-3 text-sm">
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-gray-800 font-semibold">Rs {subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-2">
                    <span className="text-gray-800 font-semibold">Total</span>
                    <span className="text-[#1E40AF] font-bold text-base">
                      Rs {parseFloat(quotation.total_amount || subtotal).toFixed(2)}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">
                    GST will be applied at invoice time.
                  </p>
                </div>
              </div>
            </div>

            {quotation.notes && (
              <div className="mb-8">
                <div className="text-sm text-gray-500 font-semibold mb-1">Notes</div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{quotation.notes}</div>
              </div>
            )}

            <div className="mt-16 grid grid-cols-2 gap-12">
              <div>
                <div className="border-t border-gray-400 pt-2 text-xs text-gray-500 text-center">
                  Prepared By (Signature)
                </div>
              </div>
              <div>
                <div className="border-t border-gray-400 pt-2 text-xs text-gray-500 text-center">
                  Customer Acceptance (Signature)
                </div>
              </div>
            </div>

            <div className="mt-10 pt-4 border-t border-gray-200 text-center text-xs text-gray-400">
              This is a quotation, not a tax invoice. Prices valid until the expiry date.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuotationPrint;
